package org.evoleq.optics.transform

import org.evoleq.math.*
import org.evoleq.optics.lens.Lens

/**
 * Multiply a lens with a writer to transform and compose data structures.
 *
 * @param W The type of the whole structure.
 * @param P The type of the part within the structure that the lens focuses on.
 * @param Q The type of the part within the structure that the writer writes to.
 * @param writer The writer component to be combined with the lens.
 * @return A writer that operates on the whole structure W to transform and output type Q.
 */
operator fun <W, P, Q> Lens<W, P>.times(writer: Writer<P, Q>): Writer<W, Q> = this timesW writer

/**
 * Multiply a `Lens` with a `Writer` to produce a new `Writer`. The resulting `Writer` allows
 * modifications to the part of a structure focused by the `Lens`, using the provided `Writer`
 * transformation for that part.
 *
 * @param W The type of the entire structure.
 * @param P The type of the part within the structure being focused by the `Lens`.
 * @param Q The type produced by the transformation applied by the `Writer` to the focused part.
 * @param writer The `Writer` to apply to the part of the structure focused by the `Lens`.
 * @return A new `Writer` that, when invoked, applies the transformation to the focused part of
 *         the structure and updates the structure accordingly.
 */
@MathDsl
infix fun <W, P, Q> Lens<W, P>.timesW(writer: Writer<P, Q>): Writer<W, Q> = {
        q -> {w -> set(writer(q)(get(w)))(w)}
}

/**
 * Merges the existing list focused by the `Lens` with another list, using the provided equality
 * function to determine which items are considered equal.
 *
 * @param consideredEqual A function that takes two elements of type `P` and returns a boolean
 *                         indicating whether the two elements are considered equal.
 * @return A `Writer` that produces a list of merged elements when applied on the wrapped structure.
 */
@MathDsl
inline infix fun <W, reified P> Lens<W, List<P>>.merge(crossinline consideredEqual: (P, P)->Boolean): Writer<W, List<P>> =
    this timesW Writer{inc -> {given -> given.merge(inc,consideredEqual)}}

/**
 * Injects a `Writer` into the composite structure represented by the `Lens`.
 *
 * @param W The type of the whole structure accessed and updated by the `Lens`.
 * @param P The type of the elements in the list contained within the structure.
 * @param Q The type of the transformed list elements after applying the `Writer`.
 * @param w The `Writer` that transforms elements of type `P` to type `Q`.
 * @param select A selector function that takes an element of type `P` and a list of `Q`,
 *               returning the specific `Q` to be applied to the writer.
 * @return A `Writer` that writes a list `List<Q>` of qs to W by applying the writer w to each element p of the ps in W.
 */
@MathDsl
@Throws(LensWriterException::class)
fun <W, P, Q> Lens<W, List<P>>.inject( write: Writer<P, Q>,select: (P, List<Q>) -> Q): Writer<W, List<Q>> = { qs: List<Q> -> { w: W ->
    set(get(w).map { p: P -> write(try{select(p, qs)} catch (_: Exception){throw LensWriterException.InvalidSelector})(p)})(w)
}}

/**
 * Injects a transformation function into a `Lens` that focuses on a `List` structure, enabling a composition
 * with a `Writer` to produce a transformed `Writer` for the whole structure.
 *
 * **Important note:**
 *
 * Using a 'Writer<P, Q?>' with nullable 'part' allows us to model the case, where the qs refer to a subset of ps only,
 * i.e., where we do NOT have a p for each q and the selection would fail.
 *
 * **Example**
 * ```
 * val writer: Writer<P, Q?> = Writer{
 *      q: Q? -> {p -> when{
 *          q == null -> p
 *          else -> TODO(inject q into p)
 *      }}
 * }
 * ```
 *
 * @param W The type of the whole structure.
 * @param P The type of the elements in the focused list within the structure.
 * @param Q The output type produced by the injected writer transformation.
 * @param w A `Writer` that operates on elements of the list with type `P`.
 * @return A higher-order function that, given a selector function of type `(P, List<Q>) -> Q`, produces a
 *         `Writer` that describes transformations on the whole structure `W` and produces
 *         a list of `Q` as the resulting output.
 */
@MathDsl
@Throws(LensWriterException.InvalidSelector::class)
infix fun <W, P, Q> Lens<W, List<P>>.inject(w: Writer<P, Q>): ((P, List<Q>) -> Q) -> Writer<W, List<Q>> = {select -> { qs: List<Q> -> { w: W ->
    this@inject.set(this@inject.get(w).map { p: P -> w(select(p, qs))(p) })(w)
}}}

/**
 * Composes a function that creates a `Writer` instance using a selector function to process a pair of inputs.
 *
 * **Important note:**
 *
 * Using a 'Writer<P, Q?>' with nullable 'part' allows us to model the case, where the qs refer to a subset of ps only,
 * i.e., where we do NOT have a p for each q and the selection would fail.
 *
 * **Example**
 * ```
 * val writer: Writer<P, Q?> = Writer{
 *      q: Q? -> {p -> when{
 *          q == null -> p
 *          else -> TODO(inject q into p)
 *      }}
 * }
 * ```
 *
 * @param select the selector function that processes a value of type `P` and a list of type `Q` to produce a value of type `Q`.
 * @return a `Writer` instance that wraps a list of type `Q`.
 */
@MathDsl
@Throws(LensWriterException.InvalidSelector::class)
infix fun <W, P, Q> (((P, List<Q>) -> Q) -> Writer<W, List<Q>>).by(select: (P, List<Q>) -> Q): Writer<W, List<Q>> = this(select)

/**
 * Transforms a `Writer<P, Q>` into a `Writer<List<P>, List<Q>>` by applying a selection function
 * to handle individual elements of `P` and `Q`.
 *
 * **Important note:**
 *
 * Using a 'Writer<P, Q?>' with nullable 'part' allows us to model the case, where the qs refer to a subset of ps only,
 * i.e., where we do NOT have a p for each q and the selection would fail.
 *
 * **Example**
 * ```
 * val writer: Writer<P, Q?> = Writer{
 *      q: Q? -> {p -> when{
 *          q == null -> p
 *          else -> TODO(inject q into p)
 *      }}
 * }
 * ```
 *
 * @param select A function that determines how to map an element of type `P` and a list of type `Q`
 *               to a specific element of type `Q`.
 * @return A `Writer` that processes lists of `P` and `Q` based on the provided `select` function.
 */
@MathDsl
@Throws(LensWriterException.InvalidSelector::class)
infix fun <P, Q> Writer<P, Q>.liftBy(select: (P, List<Q>) -> Q): Writer<List<P>, List<Q>> = Writer{
    qs: List<Q> -> {ps: List<P> -> ps.map { p: P -> write(
    try { select(p, qs)} catch(_: Exception){ throw LensWriterException.InvalidSelector})(p)
    }}
}

sealed class LensWriterException(override val message: String) : Exception(message) {
    data object InvalidSelector : LensWriterException("Selector invalid")
}

/**
 * Adds a new element to the list focused by the current `Lens`.
 *
 * This extension function creates a `Writer` that, given a new element of type `P` to add,
 * modifies the list within the structure of type `W` by appending the element while preserving
 * immutability.
 *
 * @return A `Writer` that appends an element to the focused list within the structure of type `W`.
 */
fun <W, P> Lens<W, List<P>>.add(): Writer<W, P> = Writer{
    p -> { w -> set(get(w) + listOf(p) )(w)}
}

fun <W, P> Lens<W, List<P>>.addList(): Writer<W, List<P>> = Writer{
        ps -> { w -> set(get(w) + ps )(w)}
}

fun <W, P> Lens<W, List<P>>.addMissing(equal: (old: P, new: P)-> Boolean): Writer<W, List<P>> = Writer{
        ps -> { w ->
            val oldItems = get(w)
            set(oldItems + ps.filter { p -> oldItems.none { equal(it, p) } } )(w)
        }
}


/**
 * Removes elements from a list within a structure using the provided `removeWhen` predicate.
 * This method operates through the lens, transforming the original structure by removing
 * elements that match the specified condition while maintaining immutability.
 *
 * @param removeWhen A predicate function that determines whether an element should be removed.
 *                   It takes elements of type `P` as input and returns `true` for elements to be removed.
 * @return A `Writer` that, when executed, updates the structure by removing elements matching the predicate.
 */
fun <W, P> Lens<W, List<P>>.removeWhen(removeWhen: (P)->Boolean): Writer<W, Boolean> = Writer{
    bool -> { w -> when(bool){
        true -> set(get(w).filterNot { removeWhen(it) } )(w)
        else -> w
    } }
}



/**
 * Updates the element in the list-like structure within a `Lens`, replacing the first element
 * that matches the `compare` condition with a new value.
 *
 * @param compare A predicate function used to locate the element to be updated within the list.
 *                Takes an updated element (updatedData) and current list element (listItem) as parameters
 *                and returns a Boolean indicating if the element matches the condition.
 * @return A `Writer` that, when invoked with a new value of type `P` and the original structure
 *         of type `W`, produces a new structure of type `W` with the specified update applied.
 */
fun <W, P> Lens<W, List<P>>.update(compare: (updatedData: P, listItem: P) -> Boolean): Writer<W, P> =
    Writer { updatedData -> { w -> with(get(w)) list@{
        val start = this@list.takeWhile { listItem -> !compare(updatedData, listItem) }
        val result = when {
            start.size == this@list.size -> start
            else -> start + listOf(updatedData) + dropWhile { listItem ->
                !compare( updatedData, listItem )
            }.drop(1)
        }
        set( result ) (w) }
    }
}

/**
 * Updates all elements of a list within a structure using specific comparison logic.
 *
 * This method retrieves the current list from the structure, compares each item
 * in the list with the provided set of updated items using the given comparison function,
 * and replaces matching items in the list with their updated versions. If no matches are found
 * for a given item, the original item is retained in the list. The new list is then stored
 * back into the structure, returning the updated structure along with the modified list.
 *
 * @param compare A function that determines whether an updated item matches an existing item in the list.
 *                It takes two parameters: the updated item and the existing list item.
 * @return A `Writer` that, when provided with a collection of updated items and a structure,
 *         returns the updated structure and the new list of items.
 */
fun <W, P> Lens<W, List<P>>.updateAll(compare: (updatedData: P, listItem: P) -> Boolean): Writer<W, List<P>> = Writer { updatedItems -> { w ->
    val storedList = get(w)
    val updatedList = mutableListOf<P>()
    storedList.forEach {  storedItem ->
        val replacement = updatedItems.firstOrNull { compare(it, storedItem) }
        if(replacement != null) updatedList.add(replacement) else updatedList.add(storedItem)
    }
    set( updatedList ) (w)
} }

/**
 * Updates or inserts a collection of elements (`List<P>`) into an existing list within a
 * larger structure `W`, based on a comparison function.
 *
 * This method ensures that:
 * 1. Elements in the existing list are replaced if they satisfy the comparison function
 *    with any of the provided updated elements.
 * 2. Updated elements that do not already exist in the stored list (based on the comparison function)
 *    are appended to the list.
 *
 * The operation is performed immutably within the context of the `Lens`,
 * leveraging functional programming constructs.
 *
 * @param compare A lambda function that determines if an updated element matches an element
 *                in the existing list. Should return `true` if the two elements match,
 *                and `false` otherwise.
 * @return A `Writer` that, when applied, produces an updated list (`List<P>`) within the context
 *         of the larger structure `W`.
 */
fun <W, P> Lens<W, List<P>>.upsertAll(compare: (updatedData: P, listItem: P) -> Boolean): Writer<W, List<P>> = Writer { updatedItems -> { w ->
    val storedList = get(w)
    val updatedList = mutableListOf<P>()
    storedList.forEach {  storedItem ->
        val replacement = updatedItems.firstOrNull { compare(it, storedItem) }
        if(replacement != null) updatedList.add(replacement) else updatedList.add(storedItem)
    }
    updatedItems.filterNot{ updatedList.contains(it) }.forEach { updatedItem -> updatedList.add(updatedItem) }
    set( updatedList ) (w)
} }


fun <W, P> seq(writers: Sequence<Writer<W, P>>): Writer<W, P> = Writer { updatedItem ->
    when(val initial = writers.firstOrNull()) {
        null -> {w:W -> w}
        else -> {
            val others = writers.drop(1)
            others.fold(initial(updatedItem)) { acc, next -> next(updatedItem) o acc }
        }
    }
}


fun <W, P> seq(vararg writers: Writer<W, P>): Writer<W, P> = seq(writers.asSequence())
